import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getVoicesBufferChannels,
  createVoicesCompanyPage,
  deleteVoicesCompanyPage,
  getOwnVoicesProfile,
  getVoicesCompanyPages,
  getVoicesPeople,
  updateOwnVoicesProfile,
  updateVoicesCompanyPage,
  updateVoicesPerson,
} from "../Api/voicesProfiles";
import {
  VoicesBufferChannels,
  VoicesCompanyPage,
  VoicesCompanyPagePayload,
  VoicesPerson,
  VoicesPublishingSettings,
} from "../Models/voicesProfiles";

export const useOwnVoicesProfile = () => {
  return useQuery<VoicesPerson, Error>({
    queryKey: ["voicesOwnProfile"],
    queryFn: () => getOwnVoicesProfile(),
    retry: false,
  });
};

export const useUpdateOwnVoicesProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (preferences: string) => updateOwnVoicesProfile(preferences),
    onSuccess: (profile) => {
      queryClient.setQueryData(["voicesOwnProfile"], profile);
    },
  });
};

export const useVoicesPeople = () => {
  return useQuery<VoicesPerson[], Error>({
    queryKey: ["voicesPeople"],
    queryFn: () => getVoicesPeople(),
  });
};

export const useUpdateVoicesPerson = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      personId: number;
      data: VoicesPublishingSettings;
    }) => updateVoicesPerson(variables.personId, variables.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["voicesPeople"] });
      queryClient.invalidateQueries({ queryKey: ["voicesBufferChannels"] });
    },
  });
};

export const useVoicesCompanyPages = () => {
  return useQuery<VoicesCompanyPage[], Error>({
    queryKey: ["voicesCompanyPages"],
    queryFn: () => getVoicesCompanyPages(),
  });
};

export const useSaveVoicesCompanyPage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      pageId: number | null;
      data: VoicesCompanyPagePayload;
    }) =>
      variables.pageId === null
        ? createVoicesCompanyPage(variables.data)
        : updateVoicesCompanyPage(variables.pageId, variables.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["voicesCompanyPages"] });
      queryClient.invalidateQueries({ queryKey: ["voicesBufferChannels"] });
    },
  });
};

export const useDeleteVoicesCompanyPage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pageId: number) => deleteVoicesCompanyPage(pageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["voicesCompanyPages"] });
      queryClient.invalidateQueries({ queryKey: ["voicesBufferChannels"] });
    },
  });
};

export const useVoicesBufferChannels = () => {
  return useQuery<VoicesBufferChannels, Error>({
    queryKey: ["voicesBufferChannels"],
    queryFn: () => getVoicesBufferChannels(false),
    staleTime: 5 * 60 * 1000,
  });
};

export const useRefreshVoicesBufferChannels = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => getVoicesBufferChannels(true),
    onSuccess: (channels) => {
      queryClient.setQueryData(["voicesBufferChannels"], channels);
    },
  });
};
